<template>
  <SfSidebar
    :visible="isMicrocartOpen && isMicrocartVisible"
    class="sf-sidebar--right sf-sidebar--icon o-microcart-panel"
    :class="[cartUpdating ? 'cart-updating' : '']"
    @close="$store.commit('ui/setMicrocart')"
  >
    <span v-if="cartUpdating === true" class="loading-mask"><span class="loader" /></span>

    <h3 class="my-cart__title">
      Warenkorb
    </h3>
    <CartModeHeader />

    <!--<template v-if="totalItems" #content-top>
      <SfProperty
        class="sf-property--large"
        :name="$t('Total items')"
        :value="productsCount"
      />
    </template>-->

    <transition name="fade" mode="out-in">
      <div v-if="totalItems" key="my-cart" class="my-cart">
        <div class="collected-product-list">
          <transition-group name="fade" tag="div">
            <SfCollectedProduct
              v-for="product in productsInCart"
              :key="product.id"
              :image="getThumbnailForProductExtend(product)"
              :title="product.name"
              :link="product.link"
              :regular-price="getProductPrice(product).regular"
              :special-price="getProductPrice(product).special"
              :selling-unit="product.vk_einheit"
              :stock="10"
              :max-quantity="orderableQty(product)"
              :qty="product.qty"
              :type="product.type_id"
              class="collected-product"
              @input="changeQuantity(product, $event)"
            >
              <template #configuration>
                <div class="collected-product__properties">
                  <SfProperty :name="$t('SKU')" :value="product.sku" />
                  <SfProperty
                    v-for="(property, index) in getProductOptions(product)"
                    :key="`${property.label}-${index}`"
                    :name="property.label"
                  >
                    <template #value>
                      <span class="sf-property__value" v-html="property.value" />
                    </template>
                  </SfProperty>
                </div>
                <cart-availability :product="product" ref="cart-availability" @mounted="onAvailability" @availability="onAvailability" />
              </template>
              <template #remove>
                <SfCircleIcon
                  icon="cross"
                  aria-label="Remove"
                  class="sf-circle-icon--small sf-collected-product__remove sf-collected-product__remove--circle-icon"
                  @click="removeHandler(product)"
                />
                <SfButton
                  class="sf-button--text sf-collected-product__remove sf-collected-product__remove--text"
                  @click="removeHandler(product)"
                >
                  {{ $t('Remove') }}
                </SfButton>
              </template>
              <template #actions />
              <template #more-actions>
                <span />
              </template>
            </SfCollectedProduct>
          </transition-group>
        </div>
      </div>
      <div v-else key="empty-cart" class="empty-cart">
        <div class="empty-cart__banner">
          <SfImage
            :src="require('@storefront-ui/shared/icons/empty_cart.svg')"
            :alt="$t('Your bag is empty')"
            class="empty-cart__image"
          />
          <SfHeading
            :title="$t('Your bag is empty')"
            :subtitle="$t('Looks like you haven’t added any items to the bag yet. Start shopping to fill it in.')"
            :level="2"
            class="empty-cart__heading"
          />
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div v-if="totalItems" class="my-cart__bottom">
        <SfProperty
          class="sf-property--full-width my-cart__total-price"
          v-for="total in totals"
          :key="total.code"
        >
          <template #name>
            <span class="sf-property__name">{{ total.title }}</span>
          </template>
          <template #value>
            <SfPrice :regular="total.value | price" v-if="total.code !== 'shipping'" />
            <SfPrice :regular="platformTotals.base_shipping_incl_tax | price" v-if="total.code === 'shipping'" />
          </template>
        </SfProperty>

        <APromoCode />

        <SfButton
          class="sf-button--full-width color-secondary"
          @click.native="goToCheckout"
          :disabled="isCheckoutDisabled"
        >
          {{ $t("Go to checkout") }}
        </SfButton>
      </div>
      <div v-else>
        <SfButton
          class="sf-button--full-width color-primary"
          @click.native="startShopping"
        >
          {{ $t("Start shopping") }}
        </SfButton>
      </div>
    </transition>
  </SfSidebar>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { localizedRoute } from '@vue-storefront/core/lib/multistore';
import { onlineHelper } from '@vue-storefront/core/helpers';
import { getThumbnailForProduct } from '@vue-storefront/core/modules/cart/helpers';
import { getProductPrice, getProductPriceFromTotals } from 'theme/helpers';
import VueOfflineMixin from 'vue-offline/mixin';
import onEscapePress from '@vue-storefront/core/mixins/onEscapePress';
import CartModeHeader from 'src/modules/vsf-inventory-sources/components/CartModeHeader';
import CartAvailability from 'src/modules/vsf-inventory-sources/components/CartAvailability';
import { resolveStock, getOrderableQty } from 'src/modules/vsf-inventory-sources/helpers/stockResolver';
import APromoCode from 'theme/components/atoms/a-promo-code';

import {
  SfButton,
  /* SfCollectedProduct, */
  SfProperty,
  SfPrice,
  SfImage,
  SfHeading,
  SfSidebar,
  SfCircleIcon
} from '@storefront-ui/vue';
import SfCollectedProduct from 'theme/components/organisms/o-collected-product';

export default {
  components: {
    SfButton,
    SfCollectedProduct,
    SfProperty,
    SfPrice,
    SfImage,
    SfHeading,
    SfSidebar,
    SfCircleIcon,
    CartModeHeader,
    CartAvailability,
    APromoCode
  },
  mixins: [VueOfflineMixin, onEscapePress, SfSidebar],
  data () {
    return {
      isMicrocartVisible: false,
      allAvailable: false,
      cartUpdating: false,
      enforcingStock: false,
      enforceAttempts: 0
    };
  },
  computed: {
    ...mapState({
      isMicrocartOpen: state => state.ui.microcart,
      platformTotals: state => state.cart.platformTotals
    }),
    ...mapGetters({
      productsInCart: 'cart/getCartItems',
      totals: 'cart/getTotals',
      isPickup: 'inventorySources/getIsPickup'
    }),
    cartLimitKey () {
      const items = Array.isArray(this.productsInCart) ? this.productsInCart : []
      const stockCount = (this.$store.state.inventorySources.stockItems || []).length
      return [
        this.isPickup ? 1 : 0,
        stockCount,
        items.map(product => `${product.sku}:${product.qty}`).join(',')
      ].join('|')
    },
    totalItems () {
      return Array.isArray(this.productsInCart) ? this.productsInCart.length : 0;
    },
    productsCount () {
      let count = 0;
      const products = Array.isArray(this.productsInCart) ? this.productsInCart : []
      products.forEach(product => {
        count = count + parseInt(product.qty);
      });
      return count;
    },
    isCheckoutDisabled () {
      return !this.allAvailable
    }
  },
  watch: {
    isMicrocartOpen: function (newStatus, oldStatus) {
      if (!newStatus) {
        this.cartUpdating = false
      }
    },
    productsInCart () {
      this.$nextTick(() => {
        this.onAvailability()
      })
    },
    cartLimitKey () {
      this.onAvailability()
    }
  },
  methods: {
    closeMicrocartExtend () {
      this.$store.dispatch('ui/closeMicrocart')
    },
    onEscapePress () {
      this.closeMicrocartExtend();
    },
    startShopping () {
      this.$router.push(localizedRoute('/'));
      this.closeMicrocartExtend();
    },
    getThumbnailForProductExtend (product) {
      return getThumbnailForProduct(product);
    },
    getProductPrice (product) {
      if (onlineHelper.isOnline && !this.hasServerPrice(product)) {
        return {
          regular: null,
          special: null
        }
      }

      return onlineHelper.isOnline && product.totals && product.totals.options
        ? getProductPriceFromTotals(product)
        : getProductPrice(product);
    },
    hasServerPrice (product) {
      if (!product || !product.totals) return false
      return typeof product.totals.row_total_incl_tax === 'number'
    },
    getProductOptions (product) {
      const rawOptions = onlineHelper.isOnline && product.totals && product.totals.options
        ? product.totals.options
        : product.options

      const options = Array.isArray(rawOptions)
        ? rawOptions
        : Object.values(rawOptions || {})

      return options
        .map((option) => {
          if (option && typeof option === 'object' && !Array.isArray(option)) {
            const label = this.normalizeOptionText(option.label || option.option_label || option.code || this.$t('Option'))
            const value = this.normalizeOptionText(option.value || option.option_value)
            return { label, value }
          }

          return {
            label: this.normalizeOptionText(this.$t('Option')),
            value: this.normalizeOptionText(option)
          }
        })
        .filter(option => !!option.value)
    },
    normalizeOptionText (value) {
      if (value === null || value === undefined) return ''
      if (Array.isArray(value)) {
        return value
          .map(item => this.normalizeOptionText(item))
          .filter(Boolean)
          .join(', ')
      }
      if (typeof value === 'object') {
        return Object.values(value)
          .map(item => this.normalizeOptionText(item))
          .filter(Boolean)
          .join(', ')
      }

      return String(value)
    },
    async removeHandler (product) {
      this.cartUpdating = true;
      await this.$store.dispatch('cart/removeItem', { product: product });
      await this.$nextTick()
      this.checkAvailability()
      this.cartUpdating = false;
    },
    goToCheckout () {
      this.$router.push(localizedRoute('/checkout'));
      this.closeMicrocartExtend();
    },
    orderableQty (product) {
      const stockItems = this.$store.state.inventorySources.stockItems || []
      if (!product || !stockItems.some(item => item.sku === product.sku)) return 0
      const orderable = getOrderableQty(resolveStock(product, true), this.isPickup)
      if (orderable.unlimited) return 0
      return orderable.qty
    },
    showDiffNotifications (diffLog) {
      const clientNotifications = diffLog && Array.isArray(diffLog.clientNotifications)
        ? diffLog.clientNotifications
        : []
      clientNotifications
        .filter(notificationData => notificationData && notificationData.type !== 'success')
        .forEach(notificationData => {
          this.$store.dispatch('notification/spawnNotification', notificationData, { root: true })
        })
    },
    async enforceStockLimits () {
      if (this.enforcingStock) return
      const products = Array.isArray(this.productsInCart) ? this.productsInCart : []
      const updates = products.filter(product => {
        const max = this.orderableQty(product)
        return max > 0 && Number(product.qty) > max
      })
      if (!updates.length) {
        this.enforceAttempts = 0
        return
      }
      if (this.enforceAttempts >= 1) return

      this.enforceAttempts += 1
      this.enforcingStock = true
      this.cartUpdating = true
      try {
        for (const product of updates) {
          const max = this.orderableQty(product)
          this.showDiffNotifications({
            clientNotifications: [{
              type: 'error',
              message: this.$t('Quantity must be below {quantity}', { quantity: max }),
              action1: { label: this.$t('OK') }
            }]
          })
          const diffLog = await this.$store.dispatch('cart/updateQuantity', {
            product,
            qty: max
          })
          this.showDiffNotifications(diffLog)
        }
      } finally {
        this.enforcingStock = false
        this.cartUpdating = false
      }
    },
    onAvailability () {
      this.checkAvailability()
      this.enforceStockLimits()
    },
    async changeQuantity (product, newQuantity) {
      this.cartUpdating = true;
      const diffLog = await this.$store.dispatch('cart/updateQuantity', {
        product: product,
        qty: newQuantity
      });
      this.showDiffNotifications(diffLog)
      await this.checkAvailability();
      this.cartUpdating = false;
    },
    checkAvailability () {
      const availabilityRefs = this.$refs['cart-availability']
      const components = Array.isArray(availabilityRefs)
        ? availabilityRefs
        : (availabilityRefs ? [availabilityRefs] : [])

      if (!components.length) {
        this.allAvailable = false
        return
      }

      this.allAvailable = components.every(component => !!(component && component.isAvailable))
    }
  },
  mounted () {
    this.isMicrocartVisible = true;
    this.$nextTick(() => {
      this.checkAvailability()
    })
  }
};
</script>

<style lang="scss" scoped>
@import "~@storefront-ui/shared/styles/helpers/breakpoints";
.o-microcart-panel {
  @include for-mobile {
    ::v-deep .sf-sidebar__aside {
      max-height: calc(100vh - var(--bottom-navigation-height));
    }
  }
  @include for-desktop {
    --sidebar-bottom-padding: var(--spacer-base);
    --sidebar-content-padding: var(--spacer-base);
  }
}
.my-cart {
  flex: 1;
  display: flex;
  flex-direction: column;
  &__total-items {
    margin: 0;
  }
  &__total-price {
    --price-font-size: var(--font-sm);
    --price-font-weight: var(--font-semibold);
    margin: 0 0 var(--spacer-xs) 0;
  }
}
.empty-cart {
  --heading-subtitle-margin: 0 0 var(--spacer-xl) 0;
  --heading-title-margin: 0 0 var(--spacer-base) 0;
  --heading-title-color: var(--c-primary);
  --heading-title-font-weight: var(--font-semibold);
  display: flex;
  flex: 1;
  align-items: center;
  flex-direction: column;
  &__banner {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    flex: 1;
  }
  &__heading {
    padding: 0 var(--spacer-base);
  }
  &__image {
    --image-width: 13.1875rem;
    margin: 0 0 var(--spacer-2xl) 0;
  }
  @include for-desktop {
    --heading-title-font-size: var(--font-xl);
  }
}
.collected-product-list {
  flex: 1;
}
.collected-product {
  --collected-product-image-background: var(--c-white);
  margin: 0 0 var(--spacer-sm) 0;
  &__properties {
    margin: var(--spacer-xs) 0 0 0;
  }
  &__actions {
    transition: opacity 150ms ease-in-out;
    opacity: var(--cp-actions-opacity, 0);
  }
  &__action {
    --button-padding: 0;
  }
  &:hover {
    --cp-actions-opacity: 1;
  }
}
</style>
